import { useState } from 'react'
import { Calendar as CalendarIcon, Link2, Pencil, Plus } from 'lucide-react'
import { ProcedurePreviewDialog } from '@/components/procedures/ProcedurePreviewDialog'
import { cn } from '@/lib/utils'
import { EventTypeChip } from './EventTypeChip'
import type { CalendarInfo, CycleScheduleEntry, ResolveEventMeta } from '@/types/equipment'

export function EmptyCellAffordance({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type='button'
      onClick={onClick}
      title={label}
      className='inline-flex h-9 w-9 items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-300 transition-colors hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-500'
    >
      <Plus className='h-4 w-4' />
    </button>
  )
}

export function CycleCell({
  entry,
  resolveMeta,
  onClick,
  procedureFrom,
}: {
  entry?: CycleScheduleEntry
  resolveMeta: ResolveEventMeta
  onClick: () => void
  /** On a unit's row: where its procedure comes from ("Room Type"), since it is linked there. */
  procedureFrom?: string
}) {
  // The procedure being previewed, from a chip's link icon (z8nrz7fb9k).
  const [preview, setPreview] = useState<{ id: string; name: string; event: string } | null>(null)
  if (!entry || entry.eventTypes.length === 0) {
    return <EmptyCellAffordance onClick={onClick} label='Configure schedule' />
  }
  return (
    <>
      {/* A div, not a button: each linked event carries a button of its own,
          and buttons cannot nest. Keyboard behaves as a button's would. */}
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick()
          }
        }}
        title='Edit schedule'
        className='group/cell flex min-w-[96px] cursor-pointer flex-col items-start gap-1.5 rounded-lg p-1 text-left transition-colors hover:bg-blue-50/70'
      >
        <div className='flex flex-wrap gap-1'>
          {entry.eventTypes.map((code) => {
            const meta = resolveMeta(code)
            return <EventTypeChip key={code} code={code} label={meta.label} color={meta.color} short />
          })}
        </div>
        {entry.note && <span className='text-[10px] font-medium text-gray-500'>{entry.note}</span>}
        {/* The procedure each event type runs on this cycle (z8nrz7f9u6), in the
            event's colour. Hover names it, click previews its forms (z8nrz7fb9k). */}
        {entry.eventTypes.map((code) => {
          const procedure = entry.procedures?.[code]
          if (!procedure) return null
          const meta = resolveMeta(code)
          const what = `${meta.label} runs ${procedure.name}`
          return (
            <button
              key={code}
              type='button'
              onClick={(e) => {
                // Previewing must not open Configure schedule, the cell's own click.
                e.stopPropagation()
                setPreview({ id: procedure.id, name: procedure.name, event: meta.label })
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className='inline-flex max-w-[160px] items-center gap-1 rounded text-[10px] font-medium text-blue-700 hover:underline'
              title={`${procedureFrom ? `${what}, linked on its ${procedureFrom}` : what}. Click to preview`}
              aria-label={`Preview ${procedure.name}, the procedure ${meta.label} runs`}
            >
              <Link2 className='h-3 w-3 shrink-0' />
              <span className='h-1.5 w-1.5 shrink-0 rounded-full' style={{ backgroundColor: meta.color }} aria-hidden />
              <span className='truncate'>{procedure.name}</span>
            </button>
          )
        })}
        <span className='inline-flex h-5 w-5 items-center justify-center rounded-md bg-blue-50 text-blue-500 opacity-0 transition-opacity group-hover/cell:opacity-100'>
          <Pencil className='h-3 w-3' />
        </span>
      </div>
      {preview && (
        <ProcedurePreviewDialog
          procedure={preview}
          context={`Runs on ${preview.event}`}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  )
}

export function CalendarCell({ info, onClick }: { info?: CalendarInfo; onClick: () => void }) {
  const count = info?.events.length ?? 0
  return (
    <button
      type='button'
      onClick={onClick}
      title={count > 0 ? 'Edit calendar events' : 'Add calendar events'}
      className='group/cal inline-flex flex-col items-center gap-1'
    >
      <span className='relative'>
        <span
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
            count > 0
              ? 'bg-blue-50 text-blue-500'
              : 'border border-dashed border-gray-300 text-gray-300 group-hover/cal:border-blue-400 group-hover/cal:bg-blue-50/50 group-hover/cal:text-blue-500',
          )}
        >
          <CalendarIcon className='h-4 w-4' />
        </span>
        {count > 0 && (
          <span className='absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-pink-600 px-1 text-[9px] font-bold text-white shadow-sm'>
            {count}
          </span>
        )}
      </span>
      {count > 0 && (
        <span className='text-[10px] text-gray-400'>
          {count} {count === 1 ? 'date' : 'dates'}
        </span>
      )}
    </button>
  )
}
